<template>
  <div class="h-full flex flex-col" v-if="education !== null">
    <h1 class="text-4xl mb-5">
      {{ education.title }} - <small>{{ education.type_string }}</small>
    </h1>
    <div id="page" @scroll="$bus.$emit('scroll')" class="content flex-grow">
      <div id="edum-description">
        <div class="section">
          <div class="scale-grid flex max-h-50 flex-row flex-wrap items-start">
            <tile
              :class="['w-full full']"
              :scale="false"
              :key="education.banner_image.url"
              :image="education.banner_image.url"
              :info="false"
              move-on-mouse-over
            />
          </div>
          <div class="scale-grid flex max-h-50 flex-row flex-wrap items-start">
            <div class="w-full px-3 flex h-full">
              <div class="tile-content">
                <p class="fill-content">
                  <span class="mb-4 block">{{ education.quote }}</span>

                  {{ education.description }}
                </p>
              </div>
            </div>
          </div>
        </div>
        <div class="-m section">
          <div v-for="info in education.informations" class="w-full flex h-full mb-6">
            <div class="tile-content">
              <h2 class="text-lg mb-3">{{ info.title }}</h2>
              <p class="fill-content">
                {{ info.description }}
              </p>
            </div>
          </div>
        </div>
      </div>
      <div class="section -m" id="edum-details">
        <div :class="[mobile ? 'flex-wrap' : '', 'flex flex-row']">
          <div :class="[mobile ? 'w-full mb-6' : 'w-1/3', 'list']">
            <div class="flex flex-row">
              <icon name="Type" class="icon self-start" />
              <div class="flex-grow">
                <h2 class="text-2xl">Type</h2>
                <p>{{ education.short_description }}</p>
              </div>
            </div>
            <div class="flex flex-row">
              <icon name="Language" class="icon self-start" />
              <div class="flex-grow">
                <h2 class="text-2xl">Language</h2>
                <p>{{ education.lang }}</p>
              </div>
            </div>
            <div class="flex flex-row">
              <icon name="Duration" class="icon self-start" />
              <div class="flex-grow">
                <h2 class="text-2xl">Duration</h2>
                <p v-if="education.duration.text">{{ education.duration.text }}</p>
                <p v-else>
                  {{ calculateDuration(education.duration) }}
                </p>
              </div>
            </div>
            <div class="flex flex-row">
              <icon name="Deadlines" class="icon self-start" />
              <div class="flex-grow">
                <h2 class="text-2xl">Deadlines</h2>
                <p v-for="deadline in education.deadlines">
                  {{ deadline.name }}{{ education.degree_type === 'masters' ? ` - ${formatDate(deadline.date)}` : '' }}
                </p>
              </div>
            </div>
            <div class="flex flex-row">
              <icon name="Location" class="icon self-start" />
              <div class="flex-grow">
                <h2 class="text-2xl">Location</h2>
                <p>{{ educationLocation(education) }}</p>
              </div>
            </div>
            <div class="flex flex-row">
              <icon name="Price" class="icon self-start" />
              <div class="flex-grow">
                <h2 class="text-2xl">Tuition</h2>
                <p v-for="price in education.prices">{{ price.name }} - €{{ price.amount }}</p>
              </div>
            </div>
            <div class="flex flex-row">
              <icon name="Scholarships" class="icon self-start" />
              <div class="flex-grow">
                <h2 class="text-2xl">Discounts</h2>
                <p v-if="education.scholarships_json.length < 1">{{ education.scholarships }}</p>
                <p v-else="education.scholarships_json.length > 0" v-for="scholarship in education.scholarships_json">
                  {{ scholarship.name }}
                  <span v-if="scholarship.amount > 0"> - €{{ scholarship.amount }} </span>
                </p>
              </div>
            </div>
          </div>
          <div :class="[mobile ? 'w-full' : 'w-2/3', 'flex flex-row']">
            <div class="list w-full">
              <div class="flex flex-row mb-8">
                <icon name="Degree" class="icon self-start" />
                <div class="flex-grow">
                  <h2 class="text-2xl">Degree</h2>
                  <p>{{ education.degree }}</p>
                </div>
              </div>

              <div class="flex flex-row mb-8">
                <icon name="Credits" class="icon self-start" />
                <div class="flex-grow">
                  <h2 class="text-2xl">Credits</h2>
                  <p>{{ education.credits.line1 }}</p>
                  <p>{{ education.credits.line2 }}</p>
                </div>
              </div>

              <div class="flex flex-row mb-8">
                <icon name="Requirements" class="icon self-start" />
                <div class="flex-grow">
                  <h2 class="text-2xl">Requirements</h2>
                  <p>{{ education.requirements }}</p>
                </div>
              </div>
              <div class="flex flex-row mb-8">
                <icon name="Team" class="icon self-start" />
                <div class="flex-grow">
                  <h2 class="text-2xl">Team</h2>
                  <ul class="list-disc pl-5">
                    <li v-for="t in education.team_members" class="role-item">
                      {{ t.role }} -
                      <span v-for="(mem, ind) in t.members" :key="`role_mem_${ind}`">
                        <span class="team-link" @click="goTo(mem.url)">{{ mem.name }}</span
                        ><template v-if="ind < t.members.length - 1">, </template>
                      </span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div v-if="getDetailsVideo()" class="">
          <div class="youtube-container mb-2">
            <youtube class="youtube-inner" :video-id="getVideoId(getDetailsVideo())" />
          </div>
        </div>
      </div>
      <div id="edum-chapters" class="mb-6">
        <div class="section" v-for="(chapter, i) in education.chapters" :key="`chapter_${i}`">
          <div class="scale-grid flex max-h-50 flex-row flex-wrap items-start">
            <tile
              :class="[mobile ? 'w-full mb-4' : education.degree_type === 'phd' ? 'w-1/3' : 'w-1/3 full-height']"
              :scale="false"
              :image="chapter.image.url"
              :info="false"
              :margin="false"
            />
            <div :class="[mobile ? 'w-full' : 'w-2/3', 'px-3 flex h-full pb-6']">
              <div class="tile-content">
                <h2 class="text-xl">{{ chapter.title1 }} - {{ chapter.title2 }}</h2>
                <p class="mb-6">{{ chapter.team }}</p>
                <p class="mb-6">{{ chapter.quote }}</p>
                <p class="mb-8">{{ chapter.description }}</p>

                <h2 v-if="education.degree_type !== 'phd'" class="text-xl">What You Will Learn</h2>
                <ul class="list-disc pl-5">
                  <li v-for="l in chapter.learn">
                    {{ l.value }}
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div id="edum-labs" class="mb-6">
        <div class="section" v-for="(lab, i) in education.labs" :key="`lab${i}`">
          <div class="scale-grid flex max-h-50 flex-row flex-wrap items-start">
            <tile
              :class="[mobile ? 'w-full mb-4' : 'w-1/3 full-height']"
              :scale="false"
              :image="lab.image.url"
              :info="false"
              :margin="false"
            />
            <div :class="[mobile ? 'w-full' : 'w-2/3', 'px-3 flex h-full pb-6']">
              <div class="tile-content">
                <h2 class="text-xl">{{ lab.title1 }} - {{ lab.title2 }}</h2>
                <p class="mb-6">{{ lab.team }}</p>
                <p class="mb-8">{{ lab.description }}</p>

                <h2 class="text-xl">What You Will Learn</h2>
                <ul class="list-disc pl-5">
                  <li v-for="l in lab.learn">
                    {{ l.value }}
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div id="edum-partners" class="section mb-0">
        <div class="scale-grid flex max-h-50 flex-row flex-wrap items-start">
          <div
            :class="[mobile ? 'w-1/2' : 'w-1/4', 'mb-5']"
            v-for="partner in education.partners"
            @click="goTo(partner.url)"
          >
            <tile class="w-full" :margin="false" :info="false" :image="partner.image.url" />
            <div class="px-3">
              <h2 class="text-xl">{{ partner.name }}</h2>
              <p>{{ partner.role }}</p>
            </div>
          </div>
        </div>
      </div>
      <div id="edum-faqs" class="section">
        <button
          v-for="(faq, index) in getFaqs()"
          @click.prevent="$modal.show('faqs', { faqs: getFaqs(), index })"
          class="w-full bg-white-25 hover:bg-dm py-2 rounded-full mb-1 px-4"
        >
          {{ faq.question }}
        </button>
        <FaqsModal />
      </div>
      <template v-if="education.data">
        <div id="edum-alumni" class="section">
          <h2 class="text-xl mb-2">{{ education.data.description_title }}</h2>
          <p class="fill-content mb-6">
            {{ education.data.description_text }}
          </p>
          <scale-grid class="mb-0 alumnus-no-padding-bottom">
            <tile
              v-for="(alumnus, index) in education.data.alumni"
              :class="[mobile ? 'w-1/4' : 'w-1/6']"
              :key="alumnus.id"
              :title="alumnus.name"
              @click="$modal.show('alumnus', { alumnus, index })"
              :image="alumnus.headshot_image.url"
              :tooltip="true"
              :always-tooltip="true"
              :info="false"
            />
          </scale-grid>
          <DMModal name="alumnus" :width="600">
            <div slot-scope="{ params }" v-if="params !== false">
              <p class="text-lg">
                {{ params.alumnus.name }}
              </p>
              <p class="mb-4 text-md">
                {{ params.alumnus.country }}
              </p>
              <p class="mb-4">
                {{ params.alumnus.program }}
              </p>
              <p class="mb-4">
                <em>{{ params.alumnus.quote }}</em>
              </p>
            </div>
          </DMModal>
        </div>
        <div id="edum-results" class="section results-negative">
          <div>
            <div v-for="video in education.data.result_videos">
              <div class="youtube-container mb-6">
                <youtube class="youtube-inner" :video-id="getVideoId(video)" />
              </div>
            </div>
          </div>
          <div>
            <scale-grid class="mb-16">
              <tile
                v-for="(image, index) in education.data.result_images"
                :class="[mobile ? 'w-1/2' : 'w-1/4']"
                :margin="true"
                :info="false"
                :key="image.id"
                :title="image.name"
                :image="image.url"
                @click="() => $refs.resultsLightbox.open(index)"
              />
            </scale-grid>
            <ImageLightbox ref="resultsLightbox" :images="resultImages" />
          </div>
        </div>
      </template>
    </div>
    <button
      class="action-btn self-end w-full bg-white-25 hover:bg-dm py-2 rounded-full mb-0"
      @click="appDoc"
      v-if="!education.is_disabled"
    >
      See Application
    </button>
    <button v-else class="action-btn self-end w-full bg-white-25 py-2 rounded-full mb-0" disabled>
      Registration Ended
    </button>
  </div>
</template>

<script>
import SingleImage from '@components/details/SingleImage';
import ImageSlides from '@components/details/ImageSlides';
import Icon from '@components/Icon';
import Tile from '@components/Tile';
import moment from 'moment';
import FaqsModal from '@components/modals/FaqsModal';
import ScaleGrid from '@components/ScaleGrid';
import ImageLightbox from '@components/ImageLightbox';
import DMModal from '@components/DMModal';

export default {
  name: 'education-details',
  components: { DMModal, ImageLightbox, ScaleGrid, FaqsModal, ImageSlides, SingleImage, Icon, Tile },
  data() {
    return {
      education: null,
      loading: true,
      mobile: this.$store.state.screenSize === 1,
      resultImages: [],
    };
  },
  watch: {
    '$store.state.screenSize': function (size) {
      this.mobile = size === 1;
    },
  },
  mounted() {
    this.getEducation(this.$route.params.slug)
      .then(({ data }) => {
        this.education = data;
        this.education.scholarships_json = Array.isArray(data.scholarships_json) ? data.scholarships_json : [];
        this.loading = false;
        this.resultImages = data.data.result_images;
      })
      .catch((e) => {
        if (e.response.status === 404) {
          this.$router.push('/404');
        }
      });

    this.$bus.$on('prev', () => {
      this.$router.push({
        name: `education-detail`,
        params: { slug: this.education.nav.prev.slug },
      });
    });

    this.$bus.$on('next', () => {
      this.$router.push({
        name: `education-detail`,
        params: { slug: this.education.nav.next.slug },
      });
    });

    this.$bus.$on('back', () => {
      this.$router.push({
        name: this.education.is_archived ? 'archive' : 'education',
      });
    });
  },
  beforeDestroy() {
    this.$bus.$off('prev');
    this.$bus.$off('next');
    this.$bus.$off('back');
  },
  methods: {
    getEducation(slug) {
      const includes = ['nav', 'data', 'data.alumni', 'data.result_images'];
      return window.axios.get(`/api/all-educations/${slug}?include=${includes.join(',')}`);
    },
    educationLocation(education) {
      if (education.is_online) {
        return `${education.online_platform}, Online`;
      } else {
        return `${education.city}, ${education.country}`;
      }
    },
    getFaqs() {
      return this.education.data ? this.education.data.faqs : this.education.faqs;
    },
    getDetailsVideo() {
      return this.education.degree_type === 'masters'
        ? this.education.data && this.education.data.details_video
        : this.education.details_video;
    },
    getVideoId(url) {
      if (!url) {
        return '';
      }

      return this.$youtube.getIdFromURL(url);
    },
    calculateDuration(duration) {
      let s = moment(duration.start);
      let e = moment(duration.end);
      let range = `${s.format('MMM yyy')} to ${e.format('MMM yyy')}`;
      return duration.months ? `${range} ${duration.months}` : range;
    },
    appDoc() {
      if (!this.education.is_app_url) {
        this.goTo(this.education.application_url);
      } else {
        this.goTo(this.education.application_doc.url);
      }
    },
    getSize(url, size) {
      let sum = 0;
      for (let i = 0; i < url.length; i++) {
        sum += url[i].charCodeAt(0) * 3;
      }

      return sum % size;
    },
  },
};
</script>
<style scoped lang="scss">
@use 'sass:math';

#router-cont {
  padding-bottom: 7.5rem !important;
}

.-m {
  margin-top: -8px;
}

.team-item {
  display: flex;
  flex-direction: row;
  cursor: pointer;
}

.team-link {
  white-space: nowrap;

  &:hover {
    cursor: pointer;
    @apply text-dm;
  }
}

.role-item {
  margin-bottom: 3px;
}

.youtube-container {
  width: 100%;
  position: relative;
}

.youtube-container:before {
  display: block;
  content: '';
  width: 100%;
  padding-top: math.div(9, 16) * 100%;
}

.youtube-inner {
  position: absolute;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  bottom: 0;
  right: 0;
}

.partners {
  flex-direction: row;
}

.results-negative {
  margin-top: -1.5rem;
}

.alumnus-no-padding-bottom {
  padding-bottom: 0;
}
</style>
